import { create } from "zustand";
import { SettingsTypeEnumSchema } from "@domain/schemas/BI";
import { State } from "@progress/kendo-data-query";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { buildRootBreadcrumbLabel } from "@presentation/stores/FileExplorerStore";
import { ExplorerBreadcrumbItem, ExplorerItem, ExplorerSelectedState, ExplorerStoreSlice } from "@presentation/components/helpers/useRemoteFolderExplorer";

interface SettingsFacetState {
  items: ExplorerItem[];
  metaState: State;
  selectedState: ExplorerSelectedState;
  breadcrumbItems: ExplorerBreadcrumbItem[];
  lastProjectId: string | null;
}

const defaultMetaState: State = { skip: 0, take: 50, sort: [{ field: "fileName", dir: "asc" }], filter: undefined };

const defaultFacetState = (): SettingsFacetState => ({
  items: [],
  metaState: defaultMetaState,
  selectedState: {},
  breadcrumbItems: [{ id: "home", text: buildRootBreadcrumbLabel(runtimeConfig.FASTA_CONTAINER_NAME), disabled: false }],
  lastProjectId: null,
});

interface SoftwareSettingsExplorerStore {
  facets: Partial<Record<SettingsTypeEnumSchema, SettingsFacetState>>;
  setFacetState: (settingsType: SettingsTypeEnumSchema, update: Partial<SettingsFacetState>) => void;
  resetFacetSelection: (settingsType: SettingsTypeEnumSchema) => void;
}

/**
 * One `useRemoteFolderExplorer`-shaped slice PER SoftwareSettings sub-type (Experiment, Report, GO,
 * Modification, ConditionSetup, ...), all in one store keyed by `settingsType` - so switching the
 * Settings tab's type-switcher dropdown preserves each type's own folder position/selection instead of
 * resetting it, while still letting every type share the same tab/grid instance.
 */
export const useSoftwareSettingsExplorerStore = create<SoftwareSettingsExplorerStore>((set) => ({
  facets: {},
  setFacetState: (settingsType, update) =>
    set((state) => ({
      facets: { ...state.facets, [settingsType]: { ...(state.facets[settingsType] ?? defaultFacetState()), ...update } },
    })),
  resetFacetSelection: (settingsType) =>
    set((state) => ({
      facets: { ...state.facets, [settingsType]: { ...(state.facets[settingsType] ?? defaultFacetState()), selectedState: {} } },
    })),
}));

/**
Builds the `ExplorerStoreSlice` the shared hook/grid expects, bound to one settings sub-type.
*/
export const useSettingsExplorerSlice = (settingsType: SettingsTypeEnumSchema): ExplorerStoreSlice => {
  const facet = useSoftwareSettingsExplorerStore((state) => state.facets[settingsType]) ?? defaultFacetState();
  const setFacetState = useSoftwareSettingsExplorerStore((state) => state.setFacetState);
  const resetFacetSelection = useSoftwareSettingsExplorerStore((state) => state.resetFacetSelection);

  return {
    items: facet.items,
    setItems: (items) => setFacetState(settingsType, { items }),
    selectedState: facet.selectedState,
    setSelectedState: (selectedState) => setFacetState(settingsType, { selectedState }),
    breadcrumbItems: facet.breadcrumbItems,
    setBreadcrumbItems: (breadcrumbItems) => setFacetState(settingsType, { breadcrumbItems }),
    resetSelectedGridData: () => resetFacetSelection(settingsType),
    lastProjectId: facet.lastProjectId,
    setLastProjectId: (lastProjectId) => setFacetState(settingsType, { lastProjectId }),
    metaState: facet.metaState,
    setMetaState: (metaState) => setFacetState(settingsType, { metaState }),
  };
};
